import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import DomPurify from "isomorphic-dompurify";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as zod from "zod";
import FormSection from "components/Form/FormSection";
import Loader from "components/Loader";
import AddressAutocompleteInput from "components/Quiz/components/AddressAutoCompleteInput";
import PhoneFieldInput from "components/Quiz/components/PhoneFieldInput";
import TextAreaInput from "components/Quiz/components/TextAreaInput";
import TextFieldInput from "components/Quiz/components/TextFieldInput";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import useMediaQuery from "components/Tiles/hooks/useMediaQuery";
import { Button } from "components/ui/button";
import { FormField, Form as FormWrapper } from "components/ui/form";
import useErrorLogger from "hooks/useErrorLogger";
import { RecordItem } from "types/common";
import { ERROR_TYPES } from "utils/constants";
import { APP_LANGUAGES } from "utils/language";
import toast from "utils/toast";
import FormFieldCells, { FormFieldCell } from "./FormFieldCells";

enum FieldType {
  TEXT_INPUT = "text",
  TEXTAREA = "textarea",
  PHONE = "phone",
  ADDRESS = "address"
}

type FormFieldProp = {
  type: FieldType;
  label: string;
  placeholder: string;
  isRequired?: boolean;
  isSameRowAsPrevious?: boolean;
};

type FormProps = {
  bgColor?: string;
  miniTitleColor?: string;
  title: string;
  subtitle: string;
  titleTextColor?: string; // New optional prop for title text color
  subtitleTextColor?: string;
  miniTitle?: string;
  buttonText: string;
  sections?: Array<{
    title?: string;
    fields?: Array<FormFieldProp>;
    showFieldsWithoutSection?: boolean;
  }>;
  webhookUrl?: string;
  redirectSlug?: string;
  language?: APP_LANGUAGES;
};

const getFormFieldKey = (field: FormFieldProp) => (field.label ? field.label.split(" ").join("_").toLowerCase() : "");
const getFormFieldsSchema = (fields: FormFieldProp[]) => {
  const schemaObject = (fields || [])?.reduce((acc: RecordItem, field) => {
    const key = getFormFieldKey(field);
    if (field.type === FieldType.PHONE) {
      acc[key] = zod
        .string()
        .max(14, {
          message: `${field.label} is too long`
        })
        .regex(/\+1[0-9]{10}$/, {
          message: `Invalid ${field.label}`
        });
    } else if (field.type === FieldType.ADDRESS) {
      acc[key] = zod
        .object({
          latitude: zod.number(),
          longitude: zod.number(),
          addressLine1: zod.string(),
          addressLine2: zod.string(),
          city: zod.string(),
          state: zod.string(),
          zipCode: zod.string()
        })
        .partial();
    } else {
      acc[key] = zod.string();
    }

    if (field.isRequired) {
      if (field.type === FieldType.ADDRESS) {
        acc[key] = acc[key].refine((value: RecordItem) => Object.values(value || {}).some((val) => !!val), {
          message: "Address is required"
        });
      } else {
        acc[key] = acc[key].min(1, {
          message: `${field.label} is required`
        });
      }
    }
    return acc;
  }, {});
  return zod.object(schemaObject);
};
const Form = ({
  bgColor,
  miniTitle,
  miniTitleColor,
  title,
  titleTextColor,
  subtitle,
  subtitleTextColor,
  buttonText = "Submit",
  sections,
  webhookUrl,
  redirectSlug,
  language = APP_LANGUAGES.ENGLISH
}: FormProps) => {
  const allFields = useMemo(() => (sections || []).flatMap((section) => section.fields || []), [sections]);
  const formSchema = getFormFieldsSchema(allFields);
  const [isLoading, setIsLoading] = useState(false);
  const formState = useForm<zod.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: allFields?.reduce((acc: RecordItem, field) => {
      if (field.type === FieldType.ADDRESS) {
        acc[getFormFieldKey(field)] = {};
      } else {
        acc[getFormFieldKey(field)] = "";
      }
      return acc;
    }, {})
  });

  const { isMobile } = useMediaQuery();
  const { logError } = useErrorLogger();
  const router = useRouter();

  const handleSubmit = async () => {
    if (isLoading) return;

    // if webhookUrl is present, send the payload to webhook
    if (webhookUrl) {
      const webhookPayload = {
        ...formState.getValues()
      };
      try {
        setIsLoading(true);
        // SEND PAYLOAD TO Make hook
        await fetch(webhookUrl, {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(webhookPayload)
        });

        setIsLoading(false);
        toast.success("Form submitted successfully");
        formState.reset(
          allFields?.reduce((acc: RecordItem, field) => {
            if (field.type === FieldType.ADDRESS) {
              acc[getFormFieldKey(field)] = {};
            } else {
              acc[getFormFieldKey(field)] = "";
            }
            return acc;
          }, {})
        );
        if (redirectSlug) {
          toast.success("Redirecting...");
          const redirectUrl = redirectSlug.startsWith("/") ? redirectSlug : `/${redirectSlug}`;
          router.push(redirectUrl);
        }
      } catch (err) {
        setIsLoading(false);
        logError({
          error: err as Error,
          source: "Tile - Form - handleSubmit",
          message: "Error sending Form info to webhook",
          type: ERROR_TYPES.CLIENT_TILE,
          url: window.location.href,
          additionalInfo: {
            webhookUrl: webhookUrl,
            payload: webhookPayload
          }
        });
        return;
      }
    }
  };

  const sectionsWithFormFieldCells = useMemo(() => {
    return (sections || []).reduce(
      (
        acc: Array<{
          title: string;
          fieldCells: FormFieldCell[];
          showFieldsWithoutSection: boolean;
        }>,
        currentSection
      ) => {
        acc.push({
          title: currentSection.title || "",
          fieldCells: (currentSection.fields || []).reduce(
            (
              acc: Array<{
                id: string;
                parent: React.ReactElement | null;
                children: (React.ReactElement | null)[];
              }>,
              currentField,
              index
            ) => {
              const key = getFormFieldKey(currentField);
              const fieldCell =
                currentField.type === FieldType.TEXTAREA ? (
                  <FormField
                    key={`${key}-${index}`}
                    control={formState.control}
                    name={key}
                    render={({ field, fieldState }) => (
                      <TextAreaInput
                        label={currentField.label}
                        placeholder={currentField.placeholder}
                        field={field}
                        fieldState={fieldState}
                      />
                    )}
                  />
                ) : currentField.type === FieldType.PHONE ? (
                  <FormField
                    key={`${key}-${index}`}
                    control={formState.control}
                    name={key}
                    render={({ field, fieldState }) => (
                      <PhoneFieldInput
                        label={currentField.label}
                        field={field}
                        fieldState={fieldState}
                        placeholder={currentField.placeholder}
                      />
                    )}
                  />
                ) : currentField.type === FieldType.ADDRESS ? (
                  <FormField
                    key={`${key}- ${index}`}
                    control={formState.control}
                    name={key}
                    render={({ field, fieldState }) => (
                      <AddressAutocompleteInput field={field} fieldState={fieldState} language={language} />
                    )}
                  />
                ) : (
                  <FormField
                    key={`${key}-${index}`}
                    control={formState.control}
                    name={key}
                    render={({ field, fieldState }) => (
                      <TextFieldInput
                        label={currentField.label}
                        field={field}
                        fieldState={fieldState}
                        placeholder={currentField.placeholder}
                      />
                    )}
                  />
                );

              if (currentField.isSameRowAsPrevious) {
                if (acc.length === 0) {
                  acc.push({
                    id: key,
                    parent: fieldCell,
                    children: []
                  });
                } else {
                  acc[acc.length - 1].children.push(fieldCell);
                }
              } else {
                acc.push({
                  id: key,
                  parent: fieldCell,
                  children: []
                });
              }
              return acc;
            },
            []
          ),
          showFieldsWithoutSection: !!currentSection.showFieldsWithoutSection
        });
        return acc;
      },
      []
    );
  }, [sections, formState.control, language]);

  return (
    <section id="Form-138" style={{ backgroundColor: bgColor }} className="relative pb-8 @container lg:pb-12">
      <div className="relative mx-auto flex w-full flex-col items-center max-sm:mt-20">
        {miniTitle && (
          <p className="mb-4 text-center text-[18px] font-semibold" style={{ color: miniTitleColor }}>
            {miniTitle}
          </p>
        )}
        {title && (
          <h1
            className="mb-6 text-balance text-center font-heading text-[42px] leading-[48px] lg:text-[52px] lg:leading-[64px]"
            style={{ color: titleTextColor }}
            dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title || "") }} // Render the HTML string
          />
        )}
        {subtitle && (
          <h2
            className="relative mx-auto mb-12 mt-6 max-w-3xl px-12 text-subtitle leading-8"
            style={{ color: subtitleTextColor }}
            dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(subtitle || "") }} // Render the HTML string
          />
        )}

        <MaxWidthWrapper className={clsx("flex max-w-screen-md flex-col items-center justify-center gap-2")}>
          <FormWrapper {...formState}>
            <div className="mx-auto flex w-full flex-col gap-2 px-2 lg:gap-3 lg:px-0">
              {sectionsWithFormFieldCells.map((section) =>
                section.showFieldsWithoutSection ? (
                  <FormFieldCells key={section.title.toLowerCase()} formFieldCells={section.fieldCells} />
                ) : (
                  <FormSection key={section.title.toLowerCase()} title={section.title} showCollapse>
                    <div className="flex w-full flex-col gap-2">
                      <FormFieldCells formFieldCells={section.fieldCells} />
                    </div>
                  </FormSection>
                )
              )}
            </div>
          </FormWrapper>
          <Button
            className="!my-1 w-fit"
            size={isMobile ? "xl" : "xlb"}
            onClick={formState.handleSubmit(() => handleSubmit())}
          >
            {isLoading && <Loader className="!h-6 !w-6" wrapperClassName="mr-2" />}
            {buttonText}
          </Button>
        </MaxWidthWrapper>
      </div>
    </section>
  );
};

export default Form;
